import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {Banner} from '@primer/react'
import {useState, useEffect, useMemo} from 'react'
import type {
  LogEntry,
  Choice,
  Command,
  ToolArgs,
  ToolCallMetadataMap,
  ToolUserRequestedMessage,
} from '../../types/session'
import {
  isCompletionUserMessage,
  isCommandGroup,
  isChatMessage,
  isCompletionChunk,
  isCompletedSession,
  isToolUserRequestedMessage,
  SessionState,
} from '../../types/session'
import {useLogs} from '../../hooks/useLogs'
import {useListCommands} from '../../hooks/useListCommands'
import {useSyncFavicon} from '../../hooks/useSyncFavicon'
import {Tool, ToolPrimaryContent} from './tool/ToolPrimitives'
import {ToolRenderer} from './tool/Tool'
import {useSessionContext} from '../../contexts/SessionContext'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import {generateWorkflowRunLink} from '../../utils/generate-actions-link'
import {BootingUpMessage, LoadingMessage} from './LoadingMessage'
import {preprocessLogEntries} from '../../utils/dedupe-logs-data'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {StickySteeringCommand} from './StickySteeringCommand'
import {ChatCommands, LogCommands} from './UserCommand'
import {getPendingCommands} from '../../utils/command-helpers'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {useSessionLogsContext} from './Context'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {agentSupportsSubagentGrouping} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import styles from './SessionLogs.module.css'
import {extractMcpToolNameMappings} from '../../utils/extract-mcp-setup-details'
import {ToolGrouping} from './tool-grouping/ToolGrouping'
import {groupByConsecutiveToolName} from './tool-grouping/grouping-strategies'
import {createGroupHeader} from './tool-grouping/header-strategies'
import {isCustomAgentSessionGroup} from '../../utils/group-agent-logs'
import {isRemoteSteerableChangedMessage} from '../../types/session-events'
import {RemoteSteeringEnabledEntry} from './RemoteSteeringEnabledEntry'
import type {CustomAgentGroupLogEntry} from '../../utils/group-agent-logs'
import {InteractivePromptLogEntry} from './InteractivePromptLogEntry'
import {CustomAgentSessionGroup} from './CustomAgentSessionGroup'
import {usePendingPrompt} from '../../hooks/use-pending-prompt'
import {
  extractPromptFromEntry,
  hasRenderableToolCall,
  isTimelineHiddenToolName,
  shouldSuppressTimedOutPermissionResumeMessage,
} from './prompt-log-utils'
import type {EntryWithContext} from './tool-grouping/types'
import {splitChoiceWithReasoningText, shouldSplitChoice} from '../../utils/split-reasoning-text'
import {stripPrMetadataTags} from '../../utils/pr-metadata-tags'
import {ThinkingBlock} from './ThinkingBlock'
import {PermissionStatusFooter, type PermissionEntryState} from './ResolvedPermission'

import {useOptimisticLogsOptional} from '../../contexts/OptimisticLogsContext'
import {TOOL_NAMES} from '../../constants/tool-names'
import {getPromptResponseResolvedPromptIds} from '../../utils/permission-prompt-helpers'
import {useSessionLoadingSource} from '../../hooks/use-session-loading-presentation'

const ASSISTANT_ROLE = 'assistant'

type SessionLogsProps = {
  logsPollingInterval: number
  nwo?: string | null
  onLogsLoadingChange?: (isLoading: boolean) => void
  loadingPresentation?: 'local' | 'page'
  suppressLocalLifecycleLoading?: boolean
}

export function SessionLogs({
  logsPollingInterval,
  nwo,
  onLogsLoadingChange,
  loadingPresentation = 'local',
  suppressLocalLifecycleLoading,
}: SessionLogsProps) {
  const {session, setMcpToolNameMappings} = useSessionContext()

  const {currentUserIsSessionOwner: currentUserOwnsSession} = useCurrentUser()

  const {variant} = useSessionLogsContext()
  const agentProfile = useAgentProfile()

  const {data: agentInfo, isLoading: isAgentInfoLoading} = useAgentInfo(session.agent_id, session.agent_slug)

  const {data: commandsData} = useListCommands({
    pollingInterval: logsPollingInterval, // Use same interval as logs
  })

  const [steeringCommandError, setSteeringCommandError] = useState<string | null>(null)

  const handleSteeringCommandSubmit = async (message: string) => {
    try {
      setSteeringCommandError(null)
      const response = await fetchCAPI({
        path: `/agents/sessions/${session.id}/commands`,
        method: 'POST',
        body: {content: message},
      })
      if (!response.ok) {
        const errorMessage = `Failed to create command: ${response.statusText}`
        setSteeringCommandError(errorMessage)
        return
      }
    } catch {
      setSteeringCommandError('Failed to submit command. Please try again.')
    }
  }

  const {
    data: logsData,
    isLoading: logsIsLoading,
    showPollingError,
  } = useLogs({
    logsPollingInterval,
    currentUserIsSessionOwner: currentUserOwnsSession,
  })
  const hasNoLoadedLogs = !logsData?.length
  const hasPendingPageHistory = loadingPresentation === 'page' && logsIsLoading && hasNoLoadedLogs
  const shouldSuppressLocalLifecycleLoading =
    (suppressLocalLifecycleLoading ?? loadingPresentation === 'page') || hasPendingPageHistory
  useSessionLoadingSource(
    showPollingError && hasNoLoadedLogs
      ? {kind: 'history', state: 'error', replacesLoading: false}
      : {kind: 'history', state: logsIsLoading && hasNoLoadedLogs ? 'pending' : 'idle'},
    {enabled: loadingPresentation === 'page'},
  )
  useSyncFavicon(session.state, {enabled: !agentProfile.interactiveSession})

  const pendingCommands = getPendingCommands(commandsData, logsData)

  const agentGroupingEnabled = agentProfile.enableAgentGrouping && agentSupportsSubagentGrouping(agentInfo?.slug)

  // For pull_request_comment sessions we render reply_to_comment tool outside of logs instead.
  const shouldFilterFinalStop = isCompletedSession(session) && session.event_type !== 'pull_request_comment'

  const {
    entries: preprocessedLogEntries,
    currentIntent,
    isWorking,
    abortedToolCallIds,
  } = useMemo(
    () =>
      preprocessLogEntries(logsData, {
        preserveAssistantNarration: false,
        groupCustomAgentSessions: agentGroupingEnabled,
        filterFinalStopMessage: shouldFilterFinalStop,
      }),
    [logsData, agentGroupingEnabled, shouldFilterFinalStop],
  )

  // Show thinking indicator optimistically when a user message is pending but not yet in logs
  // and the agent isn't already working. useCreateCommand adds an optimistic command on submit,
  // so this activates immediately. When isWorking is true, the command was sent mid-session and
  // legitimately queues — the normal "Queued for Copilot" label is appropriate in that case.
  const hasPendingUserMessage =
    agentProfile.interactiveSession &&
    !isWorking &&
    pendingCommands.some(cmd => !cmd.type || cmd.type === 'user_message')

  useEffect(() => {
    onLogsLoadingChange?.(!!logsIsLoading)
  }, [logsIsLoading, onLogsLoadingChange])

  // Filter out the pending prompt from inline display (it will be shown prominently in chat input)
  const optimisticLogs = useOptimisticLogsOptional()
  // Treat tool call IDs as resolved when either (a) the user has clicked
  // Allow / Deny on this device (optimistic in-memory state), or
  // (b) a persisted `permission_response` command exists for the prompt.
  const optimisticallyResolvedToolCallIds = useMemo(() => {
    const ids = new Set<string>(optimisticLogs?.optimisticResponses.map(r => r.toolCallId) ?? [])
    const prompts = logsData?.flatMap(entry => {
      const prompt = extractPromptFromEntry(entry)
      return prompt ? [prompt] : []
    })
    for (const id of getPromptResponseResolvedPromptIds(commandsData, prompts)) {
      ids.add(id)
    }
    return ids
  }, [optimisticLogs?.optimisticResponses, commandsData, logsData])
  const {pendingToolCallId, pendingPrompt, isExpired} = usePendingPrompt(
    logsData,
    optimisticallyResolvedToolCallIds,
    session.state,
  )
  const pendingPermissionState: PermissionEntryState | undefined =
    pendingPrompt?.toolName === 'permission_request' ? (isExpired ? 'expired' : 'pending') : undefined
  const logsToShowFiltered = useMemo(() => {
    const hasOptimisticResponse = pendingToolCallId
      ? optimisticLogs?.getOptimisticResponseForToolCall(pendingToolCallId)
      : undefined
    const hasPendingPermissionToolCall =
      pendingPermissionState !== undefined && hasRenderableToolCall(preprocessedLogEntries, pendingToolCallId)

    return preprocessedLogEntries.filter(entry => {
      if (isCustomAgentSessionGroup(entry)) {
        return true
      }

      if (Array.isArray(entry)) {
        return true
      }

      if (!pendingToolCallId) {
        return true
      }

      const prompt = extractPromptFromEntry(entry)
      const entryToolCallId = prompt?.toolCallId ?? null

      // Check if this entry is the pending prompt
      if (entryToolCallId === pendingToolCallId) {
        // If it has an optimistic response, keep it so it renders as resolved
        if (hasOptimisticResponse) {
          return true
        }

        if (prompt?.toolName === 'permission_request' && hasRenderableToolCall([entry], pendingToolCallId)) {
          return true
        }

        if (prompt?.toolName === 'permission_request' && !hasPendingPermissionToolCall) {
          return true
        }

        // Otherwise filter it out — it's shown in the chat input area
        return false
      }

      return true
    })
  }, [preprocessedLogEntries, pendingPermissionState, pendingToolCallId, optimisticLogs])

  // Extract and save MCP tool name mappings from log entries
  useEffect(() => {
    const mappings = extractMcpToolNameMappings(logsToShowFiltered)
    if (Object.keys(mappings).length > 0) {
      setMcpToolNameMappings(prevMappings => ({...prevMappings, ...mappings}))
    }
  }, [logsToShowFiltered, setMcpToolNameMappings])

  // Return early if in the queued state
  if (session.state === 'queued') {
    return (
      <>
        {!shouldSuppressLocalLifecycleLoading && <BootingUpMessage />}
        <ChatCommands commands={pendingCommands} />
      </>
    )
  }

  const workflowLink = session.workflow_run_id
    ? generateWorkflowRunLink(nwo ?? null, session.workflow_run_id)
    : undefined

  const errorBannerPrimaryAction = workflowLink ? (
    <Banner.PrimaryAction as="a" href={workflowLink}>
      View run
    </Banner.PrimaryAction>
  ) : undefined

  const EmptyStateComponent = agentProfile.slots.EmptyState
  const hasEmptyState = !!EmptyStateComponent
  const isEmptyAgentSession =
    hasEmptyState &&
    !logsIsLoading &&
    !showPollingError &&
    preprocessedLogEntries.length === 0 &&
    session.state === 'idle'

  return (
    <>
      {loadingPresentation === 'local' && logsIsLoading && !showPollingError && <LoadingMessage message="Loading…" />}
      {isEmptyAgentSession && pendingCommands.length > 0 && (
        <ChatCommands commands={pendingCommands} hideStatusText={hasPendingUserMessage} />
      )}
      {isEmptyAgentSession && pendingCommands.length === 0 && EmptyStateComponent && <EmptyStateComponent />}
      {!logsIsLoading &&
        !showPollingError &&
        logsData?.length === 0 &&
        !isEmptyAgentSession &&
        session.state !== 'in_progress' && <div>No logs to display</div>}
      {!logsIsLoading &&
        !isEmptyAgentSession &&
        ((logsData && logsData?.length > 0) || preprocessedLogEntries?.length > 0) && (
          <>
            {/* Renders user messages, tool calls, and assistant messages with tool grouping enabled */}
            <GroupedSessionLogs
              preprocessedLogEntries={logsToShowFiltered}
              commandsData={commandsData}
              logsData={logsData}
              abortedToolCallIds={abortedToolCallIds}
              pendingToolCallId={pendingToolCallId}
              pendingPermissionState={pendingPermissionState}
              suppressTimedOutPermissionResumeMessages={session.state === SessionState.TimedOut}
            />
            {/* Render pending/failed commands */}
            <ChatCommands commands={pendingCommands} hideStatusText={hasPendingUserMessage} />
          </>
        )}
      {showPollingError && !session.error?.message && (
        <Banner
          title="Error"
          hideTitle
          description="There was an error loading the logs. Please try again later."
          variant="critical"
          primaryAction={errorBannerPrimaryAction}
        />
      )}
      {session.state === 'in_progress' &&
        session.event_type !== 'chat' &&
        !shouldSuppressLocalLifecycleLoading &&
        !logsIsLoading &&
        !showPollingError &&
        !agentProfile.agentProvidesIntent && (
          <>
            {!isAgentInfoLoading &&
              (logsData && logsData.length > 0 ? (
                <LoadingMessage message={`${agentInfo?.display_name ?? 'Agent'} is working…`} />
              ) : (
                <BootingUpMessage />
              ))}
            {variant === 'default' && currentUserOwnsSession && (
              <StickySteeringCommand onSubmit={handleSteeringCommandSubmit} error={steeringCommandError} />
            )}
          </>
        )}
      {(isWorking || hasPendingUserMessage) &&
        !logsIsLoading &&
        !showPollingError &&
        agentProfile.interactiveSession && <LoadingMessage message={currentIntent ?? 'Thinking…'} />}
    </>
  )
}

type LogEntryChoiceProps = {
  choice: Choice
  isInGroup?: boolean
  isEditGroup?: boolean
  toolCallMetadata?: ToolCallMetadataMap
}

function LogEntryChoice({choice, isInGroup, isEditGroup, toolCallMetadata}: LogEntryChoiceProps) {
  const {mcpToolNameMappings} = useSessionContext()

  return renderLogEntryChoice(choice, mcpToolNameMappings, isInGroup, isEditGroup, toolCallMetadata)
}

function choiceContainsToolCall(choice: Choice, toolCallId?: string | null): boolean {
  return (
    !!toolCallId &&
    !!choice.delta.tool_calls?.some(toolCall => {
      const toolName = toolCall.function?.name ?? toolCall.custom?.name
      return toolCall.id === toolCallId && !isTimelineHiddenToolName(toolName)
    })
  )
}

function renderLogEntryChoice(
  choice: Choice,
  mcpToolNameMappings: ReturnType<typeof useSessionContext>['mcpToolNameMappings'],
  isInGroup?: boolean,
  isEditGroup?: boolean,
  toolCallMetadata?: ToolCallMetadataMap,
) {
  const toolCalls = choice.delta.tool_calls ?? []
  const tool =
    toolCalls.find(toolCall => {
      const toolName = toolCall.function?.name ?? toolCall.custom?.name
      return !isTimelineHiddenToolName(toolName)
    }) ?? toolCalls[0]
  const renderedDelta = tool && tool !== toolCalls[0] ? {...choice.delta, tool_calls: [tool]} : choice.delta
  const reasoningText = choice.delta.reasoning_text

  // Build the thinking block if reasoning_text is present
  const thinkingBlock = reasoningText ? <ThinkingBlock reasoningText={reasoningText} isInGroup={isInGroup} /> : null

  // Handle choices with no tool calls (reasoning-only or content messages)
  if (!tool) {
    if (!choice.delta.content && thinkingBlock) {
      return thinkingBlock
    }
    if (choice.delta.content && choice.delta.role === ASSISTANT_ROLE) {
      return thinkingBlock ? (
        <>
          {thinkingBlock}
          <CopilotMessage content={choice.delta.content} />
        </>
      ) : (
        <CopilotMessage content={choice.delta.content} />
      )
    }
    return thinkingBlock
  }

  const isCustomTool = !!tool?.custom
  const toolName = tool?.function?.name ?? tool?.custom?.name
  const rawArgs = isCustomTool ? tool?.custom?.input : tool?.function?.arguments

  if (!tool.id || !toolName) {
    return thinkingBlock
  }

  const parsedArgs = parseToolArgs(isCustomTool, rawArgs, toolName)
  const isMcpTool = Object.prototype.hasOwnProperty.call(mcpToolNameMappings, toolName)

  const component =
    parsedArgs === null ? (
      <Tool title={'Error parsing tool call arguments'} isError>
        <ToolPrimaryContent
          language={'text'}
          markdown={typeof rawArgs === 'string' ? rawArgs : JSON.stringify(rawArgs ?? {}, null, 2)}
        />
      </Tool>
    ) : (
      <ToolRenderer
        name={toolName}
        delta={renderedDelta}
        args={parsedArgs}
        isMcpTool={isMcpTool}
        mcpToolNameMappings={mcpToolNameMappings}
        isInGroup={isInGroup}
        isEditGroup={isEditGroup}
        metadata={tool.id ? toolCallMetadata?.[tool.id] : undefined}
      />
    )

  if (thinkingBlock) {
    return (
      <>
        {thinkingBlock}
        {component}
      </>
    )
  }

  return component
}

// Exported for testing only
export function parseToolArgs(isCustomTool: boolean, rawArgs: string | undefined, toolName?: string): ToolArgs | null {
  let parsedArgs: ToolArgs = {}
  let hasParsingError = false

  if (isCustomTool) {
    // Custom tool args are freeform strings and should not be parsed as JSON
    parsedArgs = typeof rawArgs === 'string' ? {content: rawArgs} : {}
  } else if (toolName === TOOL_NAMES.APPLY_PATCH && typeof rawArgs === 'string') {
    parsedArgs = parseApplyPatchArgs(rawArgs)
  } else if (typeof rawArgs === 'string') {
    if (rawArgs.trim() !== '') {
      try {
        // First, try to parse the raw JSON as-is so that valid pretty-printed JSON with newlines is handled correctly.
        parsedArgs = JSON.parse(rawArgs)
      } catch {
        try {
          // Fallback: defensively replace newline sequences that may appear in string literals or otherwise malformed JSON.
          // This attempts to handle cases where unescaped newlines were introduced, without breaking valid JSON.
          parsedArgs = JSON.parse(rawArgs.replace(/\r?\n/g, '\\n'))
        } catch {
          hasParsingError = true
        }
      }
    }
  }
  return hasParsingError ? null : parsedArgs
}

function parseApplyPatchArgs(rawArgs: string): ToolArgs {
  const parsed = parseJsonObject(rawArgs) ?? parseJsonObject(rawArgs.replace(/\r?\n/g, '\\n'))
  return parsed ?? {content: rawArgs}
}

function parseJsonObject(rawArgs: string): ToolArgs | undefined {
  try {
    const parsed = JSON.parse(rawArgs)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as ToolArgs
    }
  } catch {
    return undefined
  }

  return undefined
}

function CopilotMessage({content}: {content: string}) {
  const strippedContent = stripPrMetadataTags(content)

  // Don't render anything if content is empty after removing tags
  if (!strippedContent) {
    return null
  }

  return (
    <div className={styles.copilotMessage}>
      <div className={styles.copilotMessageContent}>
        <MarkdownRenderer markdown={strippedContent} />
      </div>
    </div>
  )
}

/**
 * Helper function to render session log entries with tool grouping.
 * Builds a unified timeline where choices are flattened in place,
 * allowing commands to appear in their chronological position while
 * consecutive tool calls can still be grouped together.
 */
function GroupedSessionLogs({
  preprocessedLogEntries,
  commandsData,
  logsData,
  abortedToolCallIds,
  pendingToolCallId,
  pendingPermissionState,
  suppressTimedOutPermissionResumeMessages,
}: {
  preprocessedLogEntries: CustomAgentGroupLogEntry[]
  commandsData: Command[] | undefined
  logsData: LogEntry[] | undefined
  abortedToolCallIds: Set<string>
  pendingToolCallId?: string | null
  pendingPermissionState?: PermissionEntryState
  suppressTimedOutPermissionResumeMessages: boolean
}) {
  const {mcpToolNameMappings} = useSessionContext()
  const shouldUngroupReasoningText = isFeatureEnabled('session_logs_ungroup_reasoning_text')

  // Build flattened session log items: flatten choices in place, keep other entries as-is.
  // Subagent session groups are kept as 'other' items so they render inline.
  // This preserves chronological order while enabling choice-level grouping
  // (e.g. consecutive run_setup calls grouped under "Setting up environment").
  const sessionLogItems: EntryWithContext[] = useMemo(() => {
    let otherIndex = 0
    const items: EntryWithContext[] = []

    for (const entry of preprocessedLogEntries) {
      // Subagent session groups are rendered as standalone 'other' items
      if (isCustomAgentSessionGroup(entry)) {
        items.push({
          type: 'other',
          entry,
          index: otherIndex++,
        })
        continue
      }

      const toolCallMetadata = 'tool_call_metadata' in entry ? entry.tool_call_metadata : undefined
      if (isCompletionChunk(entry) && !isChatMessage(entry)) {
        // Flatten choices from CompletionChunks
        for (const [choiceIndex, choice] of entry.choices.entries()) {
          // When flag is enabled and choice has BOTH reasoning_text AND tool_calls,
          // split into two items so reasoning_text renders outside of tool groups
          if (shouldUngroupReasoningText && shouldSplitChoice(choice)) {
            const splitChoices = splitChoiceWithReasoningText(choice)
            for (const splitChoice of splitChoices) {
              items.push({
                type: 'choice',
                choice: splitChoice,
                entryId: entry.id,
                choiceIndex,
                toolCallMetadata,
              })
            }
          } else {
            items.push({
              type: 'choice',
              choice,
              entryId: entry.id,
              choiceIndex,
              toolCallMetadata,
            })
          }
        }
      } else {
        // non-choice entries (commands, etc.)
        items.push({
          type: 'other',
          entry,
          index: otherIndex++,
        })
      }
    }

    if (!suppressTimedOutPermissionResumeMessages || !pendingToolCallId) {
      return items
    }

    return items.filter(
      (item, index) =>
        !shouldSuppressTimedOutPermissionResumeMessage({
          item,
          index,
          items,
          pendingToolCallId,
          suppressTimedOutPermissionResumeMessages,
        }),
    )
  }, [pendingToolCallId, preprocessedLogEntries, shouldUngroupReasoningText, suppressTimedOutPermissionResumeMessages])

  const groupHeaderFn = useMemo(() => createGroupHeader(mcpToolNameMappings), [mcpToolNameMappings])

  return (
    <ToolGrouping entries={sessionLogItems} groupingFn={groupByConsecutiveToolName} groupHeaderFn={groupHeaderFn}>
      {(item, groupContext) => {
        // Render subagent session groups inline
        if (item.type === 'other' && isCustomAgentSessionGroup(item.entry)) {
          return (
            <CustomAgentSessionGroup customAgentSession={item.entry}>
              <GroupedSessionLogs
                preprocessedLogEntries={item.entry.logs}
                commandsData={commandsData}
                logsData={logsData}
                abortedToolCallIds={abortedToolCallIds}
                pendingToolCallId={pendingToolCallId}
                pendingPermissionState={pendingPermissionState}
                suppressTimedOutPermissionResumeMessages={suppressTimedOutPermissionResumeMessages}
              />
            </CustomAgentSessionGroup>
          )
        }

        return (
          <GroupedLogEntryRenderer
            item={item}
            commandData={commandsData ?? []}
            isInGroup={groupContext?.isInGroup}
            isEditGroup={groupContext?.isEditGroup}
            logsData={logsData}
            abortedToolCallIds={abortedToolCallIds}
            pendingToolCallId={pendingToolCallId}
            pendingPermissionState={pendingPermissionState}
          />
        )
      }}
    </ToolGrouping>
  )
}

/**
 * Renders a single log entry in the grouped session logs view.
 * Handles both individual choices (from flattened CompletionChunks) and other entries (commands).
 *
 * This is specifically for the grouped view where choices are flattened and can receive
 * the `isInGroup` context from ToolGrouping.
 */
function GroupedLogEntryRenderer({
  item: entryWithContext,
  commandData,
  isInGroup,
  isEditGroup,
  logsData,
  abortedToolCallIds,
  pendingToolCallId,
  pendingPermissionState,
}: {
  item: EntryWithContext
  commandData: Command[]
  isInGroup?: boolean
  isEditGroup?: boolean
  logsData?: LogEntry[]
  abortedToolCallIds: Set<string>
  pendingToolCallId?: string | null
  pendingPermissionState?: PermissionEntryState
}) {
  const optimisticLogs = useOptimisticLogsOptional()
  const {session} = useSessionContext()
  if (entryWithContext.type === 'choice') {
    const permissionStatus =
      pendingPermissionState && choiceContainsToolCall(entryWithContext.choice, pendingToolCallId) ? (
        <PermissionStatusFooter state={pendingPermissionState} />
      ) : null

    return (
      <>
        <LogEntryChoice
          choice={entryWithContext.choice}
          isInGroup={isInGroup}
          isEditGroup={isEditGroup}
          toolCallMetadata={entryWithContext.toolCallMetadata}
        />
        {permissionStatus}
      </>
    )
  }

  // Handle other entry types (commands, etc.)
  const {entry} = entryWithContext
  if (isCustomAgentSessionGroup(entry)) {
    return null // Handled by GroupedSessionLogs render function
  }

  if (isRemoteSteerableChangedMessage(entry)) {
    return <RemoteSteeringEnabledEntry />
  }

  if (isToolUserRequestedMessage(entry as LogEntry)) {
    const prompt = entry as ToolUserRequestedMessage
    const optimisticResponse = optimisticLogs?.getOptimisticResponseForToolCall(prompt.toolCallId)
    return (
      <InteractivePromptLogEntry
        prompt={prompt}
        abortedToolCallIds={abortedToolCallIds}
        logsData={logsData}
        optimisticResponse={optimisticResponse}
        sessionState={session.state}
        isPending={pendingToolCallId === prompt.toolCallId}
      />
    )
  }

  if (isCommandGroup(entry)) {
    return <LogCommands rawCommands={entry} commandData={commandData} />
  }

  if (isCompletionUserMessage(entry)) {
    return <LogCommands rawCommands={[entry]} commandData={commandData} />
  }

  return null
}
